import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  DEFAULT_LANGUAGE,
  DEFAULT_CURRENCY,
  LANGUAGE_STORAGE_KEY,
  getCurrentLanguage,
  isAppLanguage,
  setCurrentCurrency,
  setCurrentLanguage,
  isAppCurrency,
  setCurrentTimezone,
  type AppCurrency,
  type AppLanguage,
} from "@/i18n/config";
import { resolveClinicTimezone } from "@/lib/clinicLocalization";
import { translateStaticText, type TranslationParameters } from "@/i18n/messages";
import type { Clinic } from "@/types/domain";

interface LanguageContextValue {
  language: AppLanguage;
  setLanguage: (language: AppLanguage) => void;
  currency: AppCurrency;
  timezone: string;
  t: (value: string, parameters?: TranslationParameters) => string;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

export function LanguageProvider({ children, clinic: selectedClinic = null }: { children: ReactNode; clinic?: Clinic | null }) {
  const [language, setLanguageState] = useState<AppLanguage>(() => {
    if (typeof window === "undefined") {
      return DEFAULT_LANGUAGE;
    }

    const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return isAppLanguage(storedLanguage) ? storedLanguage : DEFAULT_LANGUAGE;
  });
  const configuredCurrency = selectedClinic?.currency_code ?? selectedClinic?.currencyCode;
  const currency: AppCurrency = isAppCurrency(configuredCurrency) ? configuredCurrency : DEFAULT_CURRENCY;
  const timezone = resolveClinicTimezone(selectedClinic?.timezone);

  useEffect(() => {
    setCurrentLanguage(language);

    if (typeof document !== "undefined") {
      document.documentElement.lang = language;
    }

    if (typeof window !== "undefined") {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    }
  }, [language]);

  useEffect(() => {
    setCurrentLanguage(getCurrentLanguage());
  }, []);

  useEffect(() => {
    setCurrentCurrency(currency);
    setCurrentTimezone(timezone);

    if (typeof window !== "undefined") {
      window.localStorage.removeItem("fluxus-currency");
    }
  }, [currency, timezone]);

  const value = useMemo<LanguageContextValue>(
    () => ({
      language,
      setLanguage: setLanguageState,
      currency,
      timezone,
      t: (input: string, parameters?: TranslationParameters) => translateStaticText(input, language, parameters),
    }),
    [currency, language, timezone],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }

  return context;
}
